import React__default from 'react';
import { useId } from '../useId.js';
import { useSSRFocusState } from '../useSSRFocusState.js';

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]); if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

var useVisibilityToggle = function useVisibilityToggle(_ref) {
  var _ref$initiallyVisible = _ref.initiallyVisible,
      initiallyVisible = _ref$initiallyVisible === void 0 ? false : _ref$initiallyVisible,
      _ref$controlId = _ref.controlId,
      controlId = _ref$controlId === void 0 ? null : _ref$controlId,
      _ref$panelId = _ref.panelId,
      panelId = _ref$panelId === void 0 ? null : _ref$panelId,
      _ref$controlClickBeha = _ref.controlClickBehavior,
      controlClickBehavior = _ref$controlClickBeha === void 0 ? 'toggle' : _ref$controlClickBeha,
      _ref$controlFocusBeha = _ref.controlFocusBehavior,
      controlFocusBehavior = _ref$controlFocusBeha === void 0 ? 'none' : _ref$controlFocusBeha,
      _ref$controlBlurBehav = _ref.controlBlurBehavior,
      controlBlurBehavior = _ref$controlBlurBehav === void 0 ? 'none' : _ref$controlBlurBehav,
      _ref$overrideClickHan = _ref.overrideClickHandler,
      overrideClickHandler = _ref$overrideClickHan === void 0 ? false : _ref$overrideClickHan,
      _ref$onControlClick = _ref.onControlClick,
      onControlClickCallback = _ref$onControlClick === void 0 ? null : _ref$onControlClick,
      _ref$onControlFocus = _ref.onControlFocus,
      onControlFocusCallback = _ref$onControlFocus === void 0 ? null : _ref$onControlFocus,
      _ref$onControlBlur = _ref.onControlBlur,
      onControlBlurCallback = _ref$onControlBlur === void 0 ? null : _ref$onControlBlur,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled;
  var controlRef = React__default.useRef(null);
  var panelRef = React__default.useRef(null);

  var _React$useState = React__default.useState(initiallyVisible),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      visible = _React$useState2[0],
      setVisible = _React$useState2[1];

  var _useSSRFocusState = useSSRFocusState(controlRef),
      _useSSRFocusState2 = _slicedToArray(_useSSRFocusState, 2),
      hasFocus = _useSSRFocusState2[0],
      setFocus = _useSSRFocusState2[1];

  var show = function show() {
    if (disabled) {
      return;
    }

    setVisible(true);
  };

  var hide = function hide() {
    if (disabled) {
      return;
    }

    setVisible(false);
  };

  var onControlClick = function onControlClick(event) {
    // if we should show on focus, then don't toggle the visibility onClick
    // because click is going to trigger focus anyway, so we will rely on the focus handler
    if (!disabled && controlClickBehavior !== 'none' && controlFocusBehavior !== 'show' && !overrideClickHandler) {
      setVisible(function (currentState) {
        if (controlClickBehavior === 'toggle') {
          return !currentState;
        } else if (controlClickBehavior === 'show') {
          return true;
        } else {
          return currentState;
        }
      });
    } // Still allow callback to fire when disabled so user can run alternate logic


    if (onControlClickCallback !== null && typeof onControlClickCallback === 'function') {
      onControlClickCallback(event);
    }
  };

  var onControlFocus = function onControlFocus(event) {
    // Still want to bubble up focus state when hide/show is disabled
    setFocus(true); // show if not visible already

    if (!disabled && controlFocusBehavior === 'show' && !visible) {
      show();
    } // Still allow callback to fire when disabled so user can run alternate logic


    if (onControlFocusCallback !== null && typeof onControlFocusCallback === 'function') {
      onControlFocusCallback(event);
    }
  };

  var onControlBlur = function onControlBlur(event) {
    var currentTarget = event.currentTarget; // Give browser time to focus the next element

    requestAnimationFrame(function () {
      var activeWithinControl = currentTarget.contains(document.activeElement);
      var activeWithinPanel = panelRef.current && panelRef.current.contains(document.activeElement); // Check if the new focused element is a child of the original container
      // if we are still in the container then don't trigger the blur handler

      if (!activeWithinControl) {
        // Still want to bubble up focus state when hide/show is disabled
        setFocus(false); // Hide if visible

        if (!disabled && !activeWithinPanel && controlBlurBehavior === 'hide' && visible) {
          hide();
        } // Still allow callback to fire when disabled so user can run alternate logic


        if (onControlBlurCallback !== null && typeof onControlBlurCallback === 'function') {
          onControlBlurCallback(event);
        }
      }
    });
  };

  var id = useId();
  var generatedControllId = "control-".concat(id);
  var generatedPanelId = "panel-".concat(id);
  var controlProps = {
    ref: controlRef,
    id: controlId ? controlId : generatedControllId,
    onClick: onControlClick,
    onFocus: onControlFocus,
    onBlur: onControlBlur,
    hasFocus: hasFocus,
    'aria-owns': panelId ? panelId : generatedPanelId,
    'aria-controls': !disabled ? generatedPanelId : null,
    'aria-expanded': !disabled ? visible : true
  };
  var panelProps = {
    ref: panelRef,
    id: panelId ? panelId : generatedPanelId,
    'aria-labelledby': controlId ? controlId : generatedControllId,
    'aria-hidden': !disabled ? !visible : false
  };
  return {
    isVisible: !disabled ? visible : true,
    show: show,
    hide: hide,
    controlHasFocus: hasFocus,
    controlProps: controlProps,
    panelProps: panelProps
  };
};

export { useVisibilityToggle };
